/*
 *+------------------------------------------------------------------------+
 *| Licensed Materials - Property of IBM
 *| IBM Cognos Products: Explore
 *| (C) Copyright IBM Corp. 2018, 2019
 *|
 *| US Government Users Restricted Rights - Use, duplication or disclosure
 *| restricted by GSA ADP Schedule Contract with IBM Corp.
 *+------------------------------------------------------------------------+
 */

import React, { Component } from 'react';
import { observer } from 'mobx-react';
import PropTypes from 'util/PropTypes';


import './CompareTable.scss';

import CompareTableSideHeader from './CompareTableSideHeader';
import ChartDetail from './ChartDetail';
import CalculatedChartDetail from './CalculatedChartDetail';

import CompareTableDetailHeader from './CompareTableDetailHeader';

import { Label, SimpleTable, Select, TruncatedText } from 'ca-ui-toolkit';

import classnames from 'classnames';
import CompareTableDynamicCell from './CompareTableDynamicCell';

/**
* Compare Card table
* for showing summary stats
*/
@observer
export default class CompareTable extends Component {
	constructor(props) {
		super(props);
	}

	render() {
		const { category, sideHeaderList, onSwitch } = this.props.options;
		const compareModel = this.props.compareModel;

		const summaryModel = compareModel.getChartSummaryModel(category) || {};
		const applicableCalcs = summaryModel.applicableCalcs;

		const getChartSummaryStats = compareModel.summaryStatsGetter( category );
		const getChartSubHeader = compareModel.summarySubHeaderGetter( category );

		if( !applicableCalcs ) {
			return null; //not ready yet, bail out
		}
		void(compareModel.statsUpdated);  //render every time info is updated
		void(summaryModel.updated);  //render every time info is updated

		// prepare the columns

		const columns = [ new CompareTableSideHeader( { applicableCalcs }) ];

		const widgets = summaryModel.charts || [];
		widgets.forEach( (widgetInfo) => {
			const chart = new ChartDetail( { widgetInfo, summaryModel, getChartSummaryStats, getChartSubHeader });
			columns.push(chart);
		});

		columns.push( new CalculatedChartDetail( { summaryModel, getChartSummaryStats } ));

		// setup the cell, header renderers
		const filler = <div className="compareTable__subHeader"/>;

		const cellRenderer = (row, col) =>{
			const value = columns[col].cellRenderer(row);
			if(col !== 0) {
				return this._getCellwithToolTip(value);
			}
			return <div className="compareTable__cell"> <TruncatedText role="cell"  value={value}/> </div>;
		};
		const headerRenderer = (col) => {
			const header = columns[col].headerRenderer();

			if( header.type === 'Category') {
				if( sideHeaderList && sideHeaderList.length > 1 ){
					return (
						<div>
							{filler}
							<div className="columnSelect" key={col}>
								<Select
									truncated={false}
									size={'small'}
									options={sideHeaderList}
									selected={category}
									onChange={ v => onSwitch(v) }
									variant= {'frameless'}
								/>
							</div>
						</div>
					);
				} else {
					return (
						<div>
							{filler}
							<div role="columnheader" className="compareTable__header compareTable--centerAlign" key={col}>
								<TruncatedText value={sideHeaderList[0].label} />
							</div>
						</div>
					);
				}
			} else if( header.type === 'ChartDetail') {
				return <CompareTableDetailHeader header={header} column={col} filler={filler} />;
			}

			return  (
				<div>
					{filler}
					<div role="columnheader" className="compareTable__header compareTable--rightAlign">
						<TruncatedText value={header.label} />
					</div>
				</div>
			);
		};

		// render the table

		const classes = classnames({
			'compareTable__container': true
		});

		return (
			<div className={classes}>
				<SimpleTable
					rowLength = {applicableCalcs && applicableCalcs.length || 0}
					colLength = {columns.length}
					cellRenderer={cellRenderer}
					headerRenderer={headerRenderer}
				></SimpleTable>
			</div>
		);

	}

	_getCellwithToolTip( value ) {
		const toolTip = value.toolTip;
		//If the dataset has negative values, percentage calculations results are correct but can be a bit unexpected (ie: negative or > 100%).
		//Style the cell differently and append an indicator (*) to the value to encourage the user to see a tooltip about this.
		const cellValue = value.value + ((value.hasNegativeValuesIndicator) ? (value.hasNegativeValuesIndicator) : '');
		const cellStyle = (value.hasNegativeValuesIndicator) ? 'compareTable__cellWithHasNegativeValuesIndicator--rightAlign' : 'compareTable__cell--rightAlign';
		const toolTipLabelClass = 'compareTableTooltipLabel';
		if( typeof toolTip === 'string' ){
			return <CompareTableDynamicCell value={cellValue} valueUse={value.valueUse} formatSpec={value.formatSpec} className={cellStyle} tooltipValue={toolTip}></CompareTableDynamicCell>;
		} else if( toolTip ){
			const tiplist = [];
			let key = 0;
			toolTip.forEach( (line, index) => {
				if(index > 0) {
					tiplist.push( <br key={key++}/> );
					tiplist.push( <br key={key++}/> );
				}
				tiplist.push( <Label label={line} className={toolTipLabelClass} key={key++}/> );
			} );
			const tip = <div className="compareTable__toolTip">{tiplist}</div>;
			return <CompareTableDynamicCell value={cellValue} valueUse={value.valueUse} formatSpec={value.formatSpec} className={cellStyle} tooltipValue={tip}></CompareTableDynamicCell>;
		} else {
			return <CompareTableDynamicCell value={cellValue} valueUse={value.valueUse} formatSpec={value.formatSpec} className={cellStyle}></CompareTableDynamicCell>;
		}
	}
}

CompareTable.propTypes = {
	options: PropTypes.object,
	compareModel: PropTypes.object
};



// WEBPACK FOOTER //
// ./src/js/components/lwstats/CompareTable.js